import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import buttonEvents from '../../components/paf-button/paf-button.events';
import inputEvents from '../../components/paf-text-input/paf-text-input.events';
import toggleRadioEvents from '../../components/paf-toggle-radio/paf-toggle-radio.events';
import autocompleteEvents from '../../components/paf-autocomplete/paf-autocomplete.events';
import debounce from '../../utils/debounce';
import {
  formatDateForDatepicker,
  formatDateForTimepicker,
} from '../../utils/date-helper';
import { Place } from '../../api/clients/journey-service/models/Place';
import { InterfacePafAutocomplete } from '../../components/paf-autocomplete/paf-autocomplete.custom';
import { InterfaceSearchForm } from './paf-search-form.custom';

import { GetTripsByOriginAndDestinationRequest } from '../../api/clients/journey-service';
import { ApiRequestor } from '../../api-helpers/request';
import * as searchFormHelper from './paf-search-form.helper';
import { InterfaceI18nConfig } from '../paf-timetable/i18n/i18n.interface';

interface InterfaceAbAnValues {
  ab: string;
  an: string;
}

interface InterfaceAutocompleteType {
  type: 'from' | 'to' | 'via';
}

@Component({
  shadow: true,
  styleUrl: 'paf-search-form.scss',
  tag: 'paf-search-form',
})

export class PafSearchForm implements ComponentInterface {

  /**
   * The config for the main webcomponent.
   * See /src/apps/paf-timetable/i18n/i18n.ts for config
   */
  @Prop() public config!: string;

  /** Define if the search-form is in loading state */
  @Prop() public loading = false;

  /**
   * The url of the Journey Service API. No slash at the end!
   * e.g. if the Endpoint is:
   * https://journey-service-int.api.sbb.ch/v3/trips/...
   *
   * then you should set this value to:
   * https://journey-service-int.api.sbb.ch
   */
  @Prop() public journeyServiceApiUrl!: string;

  /**
   * The url of the SMAPI. No slash at the end!
   * e.g. if the Endpoint is:
   * https://b2p.api.sbb.ch:443/api/v2/prices/...
   *
   * then you should set this value to:
   * https://b2p.api.sbb.ch:443
   */
  @Prop() public smapiUrl!: string;

  /**
   * IMPORTANT: set this to false or omit the attribute in live-environment.
   * It's ment for local development only.
   */
  @Prop() public mock = false;

  /**
   * The language to use (for example for api request,for internal i18n values).
   * Acceptable values:
   * de, fr, it, en
   */
  @Prop() public language!: string;

  /**
   * If the search button should be a link, provide the href it here.
   */
  @Prop() public searchButtonLink?: string;

  /**
   * Purpose is for being able to easily grab origin, destination and via
   * when parent component needs access to these details for tracking.
   * */
  @Prop({
    mutable: true,
  }) public trackingOrigin: string;

  @Prop({
    mutable: true,
  }) public trackingDestination: string;

  @Prop({
    mutable: true,
  }) public trackingVia: string;

  @State() private _autocompleteItemsFrom: string;
  @State() private _autocompleteItemsTo: string;
  @State() private _autocompleteItemsVia: string;
  @State() private _expanded = false;
  @State() private _extended = false;
  @State() private _searchButtonClicked = false;
  @State() private _toggleRadioCheckedValue = '';
  @State() private _dateValue: string;
  @State() private _timeValue: string;
  @State() private _hasInputValues = false;

  @State() private _fromInputValue: InterfacePafAutocomplete['item'] = {
    id: false,
    name: '',
  };

  @State() private _toInputValue: InterfacePafAutocomplete['item'] = {
    id: false,
    name: '',
  };

  @State() private _viaInputValue: InterfacePafAutocomplete['item'] = {
    id: false,
    name: '',
  };

  @Listen('demand-focus')
  public handleFocusDemandFromOutside(): void {
    this._autocompleteElementFrom.dispatchEvent(new CustomEvent('demand-focus'));
  }

  @Watch('config')
  public async configWatchHandler(newValue: string): Promise<void> {
    this._config = JSON.parse(newValue);

    await this._restoreFormFromConfig(true);
  }

  /**
   * Event which will be emitted if the button is clicked.
   * This event sends trip request payload
   */
  @Event({
    bubbles: true,
    composed: true,
    eventName: 'paf-search-form_timetable-request',
  })
  public demandsTimetableRequest: EventEmitter<GetTripsByOriginAndDestinationRequest>;

  /**
   * Event which will be emitted if the button is clicked.
   * This event sends journey summary information
   */
  @Event({
    bubbles: true,
    composed: true,
    eventName: 'paf-search-form_update-summary',
  })
  public demandsJourneySummaryUpdate: EventEmitter<InterfaceSearchForm['summary']>;

  private _config: InterfaceI18nConfig;
  private _apiRequestor: ApiRequestor;
  private _autocompleteElementFrom: HTMLPafAutocompleteElement;
  private _autocompleteElementTo: HTMLPafAutocompleteElement;
  private _autocompleteElementVia: HTMLPafAutocompleteElement;
  private _dateElement: HTMLPafTextInputElement;
  private _extendedSearch: HTMLDivElement;
  private _timeElement: HTMLPafTextInputElement;
  private _buttonExpandable: HTMLPafButtonElement;
  private _toggleRadio: HTMLPafToggleRadioElement;
  private _toggleRadioMobile: HTMLPafToggleRadioElement;
  private _buttonSearch: HTMLPafButtonElement;
  private _checkboxesTransportation: HTMLPafRadioCheckboxGroupElement;
  private _checkboxesConnections: HTMLPafRadioCheckboxGroupElement;
  private _radiosAccessibility: HTMLPafRadioCheckboxGroupElement;
  private _fromDidJustBlur = false;
  private _toDidJustBlur = false;
  private _viaDidJustBlur = false;
  private _viaFieldset: HTMLFieldSetElement;

  private _abAnValues: InterfaceAbAnValues = {
    ab: 'ab',
    an: 'an',
  };

  @Element() private _element!: HTMLElement;

  private _resetAutocompleteItems = (): void => {
    this._autocompleteItemsFrom = '';
    this._autocompleteItemsTo = '';
    this._autocompleteItemsVia = '';
  };

  private _restoreFormFromConfig = async (issuedByConfigChange): Promise<void> => {

    // input from and to
    let inputFrom;
    let inputTo;
    let inputVia;

    if (issuedByConfigChange) {
      inputFrom = await searchFormHelper.getLocationNameFromDidok(this._config.inputForm.inputFrom.value, this._apiRequestor, this.language);

      inputTo = await searchFormHelper.getLocationNameFromDidok(this._config.inputForm.inputTo.value, this._apiRequestor, this.language);

      if (this._config.inputForm.extendedSearch.via.value) {
        inputVia = await searchFormHelper.getLocationNameFromDidok(this._config.inputForm.extendedSearch.via.value, this._apiRequestor, this.language);
      }

    } else {
      inputFrom = this._config.inputForm.inputFrom.value;
      inputTo = this._config.inputForm.inputTo.value;
      inputVia = this._config.inputForm.extendedSearch.via.value;
    }

    this._fromInputValue = {
      id: false,
      name: inputFrom,
    };

    this._toInputValue = {
      id: false,
      name: inputTo,
    };

    this._viaInputValue = {
      id: false,
      name: inputVia,
    };

    if (this._fromInputValue.name || this._toInputValue.name) {
      this._expanded = true;
    }

    // date & time
    this._setDateAndTime();

    // ab/an toggle
    this._toggleRadioCheckedValue = this._config.inputForm.toggleArrivalDeparture.value;

    this._hasInputValues = this._formHasInputValues();

    this.trackingOrigin = inputFrom;
    this.trackingDestination = inputTo;
    this.trackingVia = inputVia;
  };

  private _debouncedInputHandler = async (target: InterfaceAutocompleteType['type'], evt): Promise<void> => {

    if (this._fromDidJustBlur && target === 'from') {
      this._fromDidJustBlur = false;
      this._autocompleteItemsFrom = '';

      return;
    }

    if (this._toDidJustBlur && target === 'to') {
      this._toDidJustBlur = false;
      this._autocompleteItemsTo = '';

      return;
    }

    if (this._viaDidJustBlur && target === 'via') {
      this._viaDidJustBlur = false;
      this._autocompleteItemsVia = '';

      return;
    }

    const value: string = evt.detail;

    if (value.length < 1) {
      if (target === 'from') {
        this._autocompleteItemsFrom = '';
      } else if (target === 'to') {
        this._autocompleteItemsTo = '';
      } else if (target === 'via') {
        this._autocompleteItemsTo = '';
      }

      return;
    }

    if (value.length < this._config.inputForm.autocomplete.minChars) {
      return;
    }

    this._resetAutocompleteItems();

    const data = await this._apiRequestor.places({
      nameMatch: value,
    }, this.language);

    const transformedData: InterfacePafAutocomplete['item'][] = data.places.map((item: Place): InterfacePafAutocomplete['item'] => ({
      id: item.id,
      name: item.name,
    }));

    if (target === 'from') {
      this._autocompleteItemsFrom = JSON.stringify(transformedData);
    } else if (target === 'to') {
      this._autocompleteItemsTo = JSON.stringify(transformedData);
    } else if (target === 'via') {
      this._autocompleteItemsVia = JSON.stringify(transformedData);
    }

  };

  private _formHasInputValues = (): boolean => this._toInputValue.name.length > 0 && this._fromInputValue.name.length > 0;

  private _onInput = (): void => {
    if (!this._expanded) {
      this._expanded = true;
    }
  };

  private _onToFromInput = (fromTo: InterfaceAutocompleteType['type'], evt): void => {
    this._toDidJustBlur = false;
    this._fromDidJustBlur = false;
    this._viaDidJustBlur = false;

    if (fromTo === 'from') {
      this._fromInputValue.name = evt.detail;
      this._fromInputValue.id = false;
    } else if (fromTo === 'to') {
      this._toInputValue.name = evt.detail;
      this._toInputValue.id = false;
    } else if (fromTo === 'via') {
      this._viaInputValue.name = evt.detail;
      this._viaInputValue.id = false;
    }

    this._hasInputValues = this._formHasInputValues();

    if (evt.detail) {
      this._debouncedInputEvent(fromTo, evt);
    } else {
      if (fromTo === 'from') {
        this._fromDidJustBlur = true;
        this._autocompleteItemsFrom = '';
      } else if (fromTo === 'to') {
        this._toDidJustBlur = true;
        this._autocompleteItemsTo = '';
      } else if (fromTo === 'via') {
        this._viaDidJustBlur = true;
        this._autocompleteItemsVia = '';
      }
    }

    this.trackingOrigin = this._fromInputValue.name;
    this.trackingDestination = this._toInputValue.name;
    this.trackingVia = this._viaInputValue.name;
  };

  private _onToFromSwitch = (): void => {
    const from = this._fromInputValue;
    const to = this._toInputValue;

    this._toInputValue = from;
    this._fromInputValue = to;

    this.trackingOrigin = this._fromInputValue.name;
    this.trackingDestination = this._toInputValue.name;
  };

  private _onAutocompleteSelect = (fromTo: InterfaceAutocompleteType['type'], evt): void => {

    const eventDetail: InterfacePafAutocomplete['item'] = evt.detail;

    if (fromTo === 'from') {
      this._fromInputValue = eventDetail;
    } else if (fromTo === 'to') {
      this._toInputValue = eventDetail;
    } else if (fromTo === 'via') {
      this._viaInputValue = eventDetail;
    }

    this.trackingOrigin = this._fromInputValue.name;
    this.trackingDestination = this._toInputValue.name;
    this.trackingVia = this._viaInputValue.name;

  };

  private _onButtonExpandableClick = (): void => {
    this._extended = !this._extended;
  };

  private _onExtendedSearchKeyDown = (evt): void => {
    const {
      key,
    } = evt;

    if (key === 'Escape') {
      this._extended = false;
    }
  };

  private _onExtendedSearchTransitionEnd = (evt): void => {
    if (evt.propertyName === 'grid-template-rows') {
      // If the search button got pressed we skip the focus
      // handling part
      if (this._searchButtonClicked) {
        this._searchButtonClicked = false;

        return;
      }

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (this._extended) {
        this._viaFieldset.scrollIntoView({
          behavior: prefersReducedMotion
            ? 'auto'
            : 'smooth',
          block: 'start',
        });
        requestAnimationFrame(() => {
          this._viaFieldset.focus();
        });
      } else {
        this._buttonExpandable.scrollIntoView({
          behavior: prefersReducedMotion
            ? 'auto'
            : 'smooth',
          block: 'end',
        });
        requestAnimationFrame(() => {
          this._buttonExpandable.shadowRoot.querySelector('button')
            .focus();
        });
      }
    }
  };

  private _syncToggleRadios = (checkedValue): void => {
    this._toggleRadioCheckedValue = checkedValue.detail;
  };

  private _onDateTimeKeyDown = (evt): void => {
    const {
      key,
    } = evt;

    if (key !== 'Enter') {
      return;
    }

    this._onButtonSearchClick();
  };

  private _onAutocompleteKeyDown = (evt: KeyboardEvent): void => {
    const {
      key,
    } = evt;

    if (key === 'Escape') {
      evt.stopImmediatePropagation();
    }

    if (key !== 'Enter') {
      return;
    }

    if (!this._hasInputValues || this.loading) {
      return;
    }

    // if autocomplete item is selected, we don't want to issue a search
    const fromHasSelected = this._autocompleteElementFrom.hasAttribute('item-selected');
    const toHasSelected = this._autocompleteElementTo.hasAttribute('item-selected');
    const viaHasSelected = this._autocompleteElementVia.hasAttribute('item-selected');

    if (fromHasSelected || toHasSelected || viaHasSelected) {
      return;
    }

    this._fromDidJustBlur = true;
    this._toDidJustBlur = true;
    this._viaDidJustBlur = true;
    this._resetAutocompleteItems();
    this._onButtonSearchClick();

  };

  private _onAutocompleteBlurHandlerFrom = (): void => {
    this._fromDidJustBlur = true;
  };

  private _onAutocompleteBlurHandlerTo = (): void => {
    this._toDidJustBlur = true;
  };

  private _onAutocompleteBlurHandlerVia = (): void => {
    this._viaDidJustBlur = true;
  };

  // ##################################################################
  // ############# Trips search #####################
  // ##################################################################

  private _onButtonSearchClick = async (): Promise<void> => {
    // close extended search
    this._searchButtonClicked = true;

    if (this._extended) {
      this._extended = false;

      await new Promise<void>((resolve) => {
        const onTransitionEnd = (): void => {
          console.log('trans end');
          this._extendedSearch.removeEventListener('transitionend', onTransitionEnd);

          resolve();
        };

        this._extendedSearch.addEventListener('transitionend', onTransitionEnd);
      });
    }

    const ensureDidokForFrom = await searchFormHelper.getDidokFromValue(this._fromInputValue, this._apiRequestor, this.language);
    const ensureDidokForTo = await searchFormHelper.getDidokFromValue(this._toInputValue, this._apiRequestor, this.language);

    const fromId = ensureDidokForFrom.id as string;
    const toId = ensureDidokForTo.id as string;

    const vias = await searchFormHelper.getVias(this._viaInputValue, this._apiRequestor, this.language);
    const forArrival = this._toggleRadioCheckedValue === this._abAnValues.an;

    // emit trip request payload
    this.demandsTimetableRequest.emit({
      tripsByOriginAndDestinationRequestBody: {
        date: new Date(this._dateElement.value),
        destination: toId,
        forArrival,
        includeAccessibility: searchFormHelper.getAccessibility(this._radiosAccessibility, this._config),
        includeNoticeAttributes: searchFormHelper.getNoticeAttributes(this._checkboxesConnections),
        includeTransportModes: searchFormHelper.getTransportModes(this._checkboxesTransportation),
        mobilityFilter: {
          maxTransfers: searchFormHelper.isDirectConnectionChecked(this._checkboxesConnections)
            ? 0
            : 11,
        },
        origin: fromId,
        time: this._timeElement.value,
        vias,
      },
    });

    // emit summary details
    const journeySummaryObject = searchFormHelper.getJourneySummaryData({
      config: this._config,
      date: this._dateElement.value,
      destination: ensureDidokForTo.name,
      forArrival,
      language: this.language,
      origin: ensureDidokForFrom.name,
      time: this._timeElement.value,
      via: this._viaInputValue.name,
      viaDescription: this._config.inputForm.extendedSearch.via.label[this.language],
    });

    this.demandsJourneySummaryUpdate.emit(journeySummaryObject);
  };

  // ##################################################################
  // ############# END Trips Search #################
  // ##################################################################

  private _setDateAndTime = (): void => {
    let configDate = this._config.inputForm.date.value;
    let configTime = this._config.inputForm.time.value;

    if (!configDate || configDate === '') {
      configDate = formatDateForDatepicker(new Date());
    }

    if (!configTime || configTime === '') {
      configTime = formatDateForTimepicker(new Date());
    }

    this._dateValue = configDate;
    this._timeValue = configTime;
  };

  public async componentWillLoad(): Promise<void> {
    this._config = JSON.parse(this.config);
    this._apiRequestor = new ApiRequestor(this.journeyServiceApiUrl, this.smapiUrl, this.mock);
    this._debouncedInputEvent = debounce(this._debouncedInputHandler, this._config.inputForm.autocomplete.debounceInputEvent);
    this._expanded = this._config.inputForm.expanded;
    this._extended = this._config.inputForm.extendedSearch.extended;

    await this._restoreFormFromConfig(false);

  }

  private _debouncedInputEvent: any;

  private _inputHandlerFrom = this._onToFromInput.bind(this, 'from');
  private _inputHandlerTo = this._onToFromInput.bind(this, 'to');
  private _inputHandlerVia = this._onToFromInput.bind(this, 'via');
  private _onAutocompleteSelectHandlerFrom = this._onAutocompleteSelect.bind(this, 'from');
  private _onAutocompleteSelectHandlerTo = this._onAutocompleteSelect.bind(this, 'to');
  private _onAutocompleteSelectHandlerVia = this._onAutocompleteSelect.bind(this, 'via');

  public componentDidLoad(): void {
    this._element.addEventListener(inputEvents.inputEvent, this._onInput);
    this._autocompleteElementFrom.addEventListener(inputEvents.inputEvent, this._inputHandlerFrom);
    this._autocompleteElementTo.addEventListener(inputEvents.inputEvent, this._inputHandlerTo);
    this._autocompleteElementVia.addEventListener(inputEvents.inputEvent, this._inputHandlerVia);
    this._autocompleteElementFrom.addEventListener(autocompleteEvents.selectedEvent, this._onAutocompleteSelectHandlerFrom);
    this._autocompleteElementTo.addEventListener(autocompleteEvents.selectedEvent, this._onAutocompleteSelectHandlerTo);
    this._autocompleteElementVia.addEventListener(autocompleteEvents.selectedEvent, this._onAutocompleteSelectHandlerVia);
    this._autocompleteElementFrom.addEventListener('keydown', this._onAutocompleteKeyDown);
    this._autocompleteElementTo.addEventListener('keydown', this._onAutocompleteKeyDown);
    this._autocompleteElementVia.addEventListener('keydown', this._onAutocompleteKeyDown);
    this._autocompleteElementFrom.addEventListener('blur', this._onAutocompleteBlurHandlerFrom);
    this._autocompleteElementTo.addEventListener('blur', this._onAutocompleteBlurHandlerTo);
    this._autocompleteElementVia.addEventListener('blur', this._onAutocompleteBlurHandlerVia);
    this._buttonExpandable.addEventListener(buttonEvents.expandableChanged, this._onButtonExpandableClick);
    this._buttonExpandable.addEventListener(buttonEvents.expandableKeyDown, this._onExtendedSearchKeyDown);
    this._extendedSearch.addEventListener('keydown', this._onExtendedSearchKeyDown);
    this._extendedSearch.addEventListener('transitionend', this._onExtendedSearchTransitionEnd);
    this._toggleRadio.addEventListener(toggleRadioEvents.changed, this._syncToggleRadios);
    this._toggleRadioMobile.addEventListener(toggleRadioEvents.changed, this._syncToggleRadios);
    this._buttonSearch.addEventListener(buttonEvents.clicked, this._onButtonSearchClick);
    this._dateElement.addEventListener('keydown', this._onDateTimeKeyDown);
    this._timeElement.addEventListener('keydown', this._onDateTimeKeyDown);
  }

  public disconnectedCallback(): void {
    this._element.removeEventListener(inputEvents.inputEvent, this._onInput);
    this._autocompleteElementFrom.removeEventListener(inputEvents.inputEvent, this._inputHandlerFrom);
    this._autocompleteElementTo.removeEventListener(inputEvents.inputEvent, this._inputHandlerTo);
    this._autocompleteElementVia.removeEventListener(inputEvents.inputEvent, this._inputHandlerVia);
    this._autocompleteElementFrom.removeEventListener(autocompleteEvents.selectedEvent, this._onAutocompleteSelectHandlerFrom);
    this._autocompleteElementTo.removeEventListener(autocompleteEvents.selectedEvent, this._onAutocompleteSelectHandlerTo);
    this._autocompleteElementVia.removeEventListener(autocompleteEvents.selectedEvent, this._onAutocompleteSelectHandlerVia);
    this._autocompleteElementFrom.removeEventListener('keydown', this._onAutocompleteKeyDown);
    this._autocompleteElementTo.removeEventListener('keydown', this._onAutocompleteKeyDown);
    this._autocompleteElementVia.removeEventListener('keydown', this._onAutocompleteKeyDown);
    this._autocompleteElementFrom.removeEventListener('blur', this._onAutocompleteBlurHandlerFrom);
    this._autocompleteElementTo.removeEventListener('blur', this._onAutocompleteBlurHandlerTo);
    this._autocompleteElementVia.removeEventListener('blur', this._onAutocompleteBlurHandlerVia);
    this._buttonExpandable.removeEventListener(buttonEvents.expandableChanged, this._onButtonExpandableClick);
    this._buttonExpandable.removeEventListener(buttonEvents.expandableKeyDown, this._onExtendedSearchKeyDown);
    this._extendedSearch.removeEventListener('keydown', this._onExtendedSearchKeyDown);
    this._extendedSearch.removeEventListener('transitionend', this._onExtendedSearchTransitionEnd);
    this._toggleRadio.removeEventListener(toggleRadioEvents.changed, this._syncToggleRadios);
    this._toggleRadioMobile.removeEventListener(toggleRadioEvents.changed, this._syncToggleRadios);
    this._buttonSearch.removeEventListener(buttonEvents.clicked, this._onButtonSearchClick);
    this._dateElement.removeEventListener('keydown', this._onDateTimeKeyDown);
    this._timeElement.removeEventListener('keydown', this._onDateTimeKeyDown);
  }

  public render(): HTMLDivElement {
    let expandedBlockAttributes = {};
    let extendedBlockAttributes = {};

    const expandableBlockAttributes = {
      inert: true,
    };

    if (!this._expanded) {
      expandedBlockAttributes = expandableBlockAttributes;
    }

    if (!this._extended) {
      extendedBlockAttributes = expandableBlockAttributes;
    }

    const searchButtonAttributes: any = {};

    const hasLink = (this.searchButtonLink && this.searchButtonLink.length > 0) === true;

    if (hasLink) {
      searchButtonAttributes.target = '_self';
    }

    return (
      <search
        aria-label={this._config.inputForm.searchFormAssistiveLabel[this.language]}
      >
        <form
          class={{
            'form': true,
            'is-expanded': this._expanded,
          }}
        >
          {/* from + toggle + to */}
          <div
            class='from-to'
            data-testid='from-to-wrapper'
          >
            <paf-autocomplete
              helpTextUseArrowKeysToNavigate={this._config.inputForm.autocomplete.helpTextUseArrowKeysToNavigate[this.language]}
              helpTextResultsAvailable={this._config.inputForm.autocomplete.helpTextResultsAvailable[this.language]}
              inputName='from'
              inputLabel={this._config.inputForm.inputFrom.label[this.language]}
              autocompleteId='from'
              inputPlaceholder={this._config.inputForm.autocomplete.inputPlaceholder[this.language]}
              inputErrorMessage={this._config.inputForm.inputErrorMessage[this.language]}
              value={this._fromInputValue.name}
              items={this._autocompleteItemsFrom}
              data-testid='from'
              ref={(elem) => {
                this._autocompleteElementFrom = elem;
              }}
              listAssistiveText={this._config.inputForm.inputFrom.listAssistiveText[this.language]}
            />

            {[...Array(2)].map((_, index) => (
              <paf-button
                class={{
                  'not-rotated': index === 0,
                  'rotated': index === 1,
                }}
                disabled={!this._hasInputValues}
                icon={true}
                iconDescription={this._config.inputForm.switchFromToIconDescription[this.language]}
                variant='secondary'
                type='button'
                rotateIcon={index === 1}
                onClick={this._onToFromSwitch}
                data-testid={`'from-to-toggle-${index}`}
              >
                <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'><path d='m29.1 22.7-8-8H23l8.7 8.7-8.8 8.6H21l8-8H10v-1.3h19.1ZM.4 8.7l8.7 8.7H11l-8-8h19V8H2.9l8-8H9.1L.4 8.7Z'/></svg>
              </paf-button>
            ))}

            <paf-autocomplete
              helpTextUseArrowKeysToNavigate={this._config.inputForm.autocomplete.helpTextUseArrowKeysToNavigate[this.language]}
              helpTextResultsAvailable={this._config.inputForm.autocomplete.helpTextResultsAvailable[this.language]}
              inputName='to'
              inputLabel={this._config.inputForm.inputTo.label[this.language]}
              autocompleteId='to'
              inputPlaceholder={this._config.inputForm.autocomplete.inputPlaceholder[this.language]}
              inputErrorMessage={this._config.inputForm.inputErrorMessage[this.language]}
              items={this._autocompleteItemsTo}
              value={this._toInputValue.name}
              data-testid='to'
              ref={(elem) => {
                this._autocompleteElementTo = elem;
              }}
              listAssistiveText={this._config.inputForm.inputTo.listAssistiveText[this.language]}
            />
          </div>

          {/* date + toggle from tablet + time + toggle mobile */}
          <div
            class='date-time'
            data-testid='date-time-row'
            {...expandedBlockAttributes}
          >
            <div class='date-time-container'>
              <paf-text-input
                errorMessage={this._config.inputForm.inputErrorMessage[this.language]}
                inputId='search-date'
                label={this._config.inputForm.date.label[this.language]}
                name='search-date'
                required
                type='date'
                value={this._dateValue}
                data-testid='date'
                max='2100-01-01'
                min='2000-01-01'
                dateTimeIconDescription={this._config.inputForm.date.iconDescription[this.language]}
                ref={(elem) => {
                  this._dateElement = elem;
                }}
              />

              <paf-toggle-radio
                optionsName='abAnToggle'
                checkedValue={this._toggleRadioCheckedValue}
                option1value={this._abAnValues.ab}
                option2value={this._abAnValues.an}
                option1label={this._config.inputForm.toggleArrivalDeparture.labelDeparture[this.language]}
                option2label={this._config.inputForm.toggleArrivalDeparture.labelArrival[this.language]}
                legend={this._config.inputForm.toggleArrivalDeparture.description[this.language]}
                data-testid='ab-an-toggle'
                ref={(elem) => {
                  this._toggleRadio = elem;
                }}
              />

              <paf-text-input
                errorMessage={this._config.inputForm.inputErrorMessage[this.language]}
                inputId='search-time'
                label={this._config.inputForm.time.label[this.language]}
                name='search-time'
                required
                type='time'
                value={this._timeValue}
                dateTimeIconDescription={this._config.inputForm.time.iconDescription[this.language]}
                data-testid='time'
                ref={(elem) => {
                  this._timeElement = elem;
                }}
              />

              <paf-toggle-radio
                optionsName='abAnToggleMobile'
                checkedValue={this._toggleRadioCheckedValue}
                option1value={this._abAnValues.ab}
                option2value={this._abAnValues.an}
                option1label={this._config.inputForm.toggleArrivalDeparture.labelDeparture[this.language]}
                option2label={this._config.inputForm.toggleArrivalDeparture.labelArrival[this.language]}
                legend={this._config.inputForm.toggleArrivalDeparture.description[this.language]}
                data-testid='ab-an-toggle-mobile'
                ref={(elem) => {
                  this._toggleRadioMobile = elem;
                }}
              />
            </div>
          </div>

          {/* extended search */}
          <div
            class={{
              'extended-search-container': true,
              'is-extended': this._extended,
            }}
            data-testid='extended-search'
            {...extendedBlockAttributes}
            tabindex='-1'
            ref={(elem) => {
              this._extendedSearch = elem;
            }}
          >
            <div class='extended-search'>
              {/* Via */}
              <fieldset
                class='via-fieldset extended-search-block'
                ref={(elem) => {
                  this._viaFieldset = elem;
                }}
                tabindex='-1'
                data-testid='via-fieldset'
              >
                <legend
                  class='legend'
                  role='text'
                >
                  {this._config.inputForm.extendedSearch.via.title[this.language]}
                </legend>
                <paf-autocomplete
                  helpTextUseArrowKeysToNavigate={this._config.inputForm.autocomplete.helpTextUseArrowKeysToNavigate[this.language]}
                  helpTextResultsAvailable={this._config.inputForm.autocomplete.helpTextResultsAvailable[this.language]}
                  inputName='via'
                  inputLabel={this._config.inputForm.extendedSearch.via.label[this.language]}
                  autocompleteId='via'
                  inputPlaceholder={this._config.inputForm.extendedSearch.via.placeholder[this.language]}
                  inputErrorMessage={this._config.inputForm.inputErrorMessage[this.language]}
                  value={this._viaInputValue.name}
                  items={this._autocompleteItemsVia}
                  inputRequired={false}
                  data-testid='via'
                  ref={(elem) => {
                    this._autocompleteElementVia = elem;
                  }}
                />
              </fieldset>

              {/* Means of transportation */}
              <paf-radio-checkbox-group
                class='extended-search-block'
                legend={this._config.inputForm.extendedSearch.meansOfTransportation.title[this.language]}
                show-check-uncheck-all-button={true}
                check-all-button-text={this._config.inputForm.extendedSearch.selectAllButton[this.language]}
                uncheck-all-button-text={this._config.inputForm.extendedSearch.deselectAllButton[this.language]}
                data-testid='transportation'
                ref={(elem) => {
                  this._checkboxesTransportation = elem;
                }}
              >
                {this._config.inputForm.extendedSearch.meansOfTransportation.inputs.map((input) => (
                  <paf-radio-checkbox
                    label={input.label[this.language]}
                    name={this._config.inputForm.extendedSearch.meansOfTransportation.name}
                    type='checkbox'
                    value={input.value}
                    checked={input.checked}
                  ></paf-radio-checkbox>
                ))}
              </paf-radio-checkbox-group>

              {/* Connections */}
              <paf-radio-checkbox-group
                class='extended-search-block'
                legend={this._config.inputForm.extendedSearch.connections.title[this.language]}
                show-check-uncheck-all-button={true}
                check-all-button-text={this._config.inputForm.extendedSearch.selectAllButton[this.language]}
                uncheck-all-button-text={this._config.inputForm.extendedSearch.deselectAllButton[this.language]}
                data-testid='connections'
                ref={(elem) => {
                  this._checkboxesConnections = elem;
                }}
              >
                {this._config.inputForm.extendedSearch.connections.inputs.map((input) => (
                  <paf-radio-checkbox
                    label={input.label[this.language]}
                    name={this._config.inputForm.extendedSearch.meansOfTransportation.name}
                    type='checkbox'
                    value={input.value}
                    checked={input.checked}
                  ></paf-radio-checkbox>
                ))}
              </paf-radio-checkbox-group>

              {/* a11y */}
              <paf-radio-checkbox-group
                class='extended-search-block'
                legend={this._config.inputForm.extendedSearch.a11y.title[this.language]}
                show-check-uncheck-all-button={false}
                show-toggle-checkbox={true}
                toggle-checkbox-value='a11y-toggle'
                toggle-checkbox-name='a11y-toggle'
                toggle-checkbox-label={this._config.inputForm.extendedSearch.a11y.toggleTitle[this.language]}
                toggle-checkbox-checked={this._config.inputForm.extendedSearch.a11y.checked}
                data-testid='a11y'
                ref={(elem) => {
                  this._radiosAccessibility = elem;
                }}
              >
                {this._config.inputForm.extendedSearch.a11y.inputs.map((input) => (
                  <paf-radio-checkbox
                    label={input.label[this.language]}
                    name={this._config.inputForm.extendedSearch.a11y.name}
                    type='radio'
                    value={input.value}
                    checked={input.checked}
                  ></paf-radio-checkbox>
                ))}
              </paf-radio-checkbox-group>
            </div>
          </div>

          {/* extended toggle + search button */}
          <div class='buttons'>
            <paf-button
              variant='expandable'
              class='extended-search-button'
              expanded={this._extended}
              text={this._config.inputForm.buttonExpandedSearch[this.language]}
              data-testid='extended-search-toggle'
              ref={(elem) => {
                this._buttonExpandable = elem;
              }}
            />

            <paf-button
              variant='primary'
              text={this._config.inputForm.searchButton[this.language]}
              type='button'
              data-testid='search-button'
              disabled={!this._hasInputValues || this.loading}
              isLink={hasLink}
              forcePreventDefault={true}
              href={this.searchButtonLink}
              {...searchButtonAttributes}
              ref={(elem) => {
                this._buttonSearch = elem;
              }}
            />
          </div>
        </form>
      </search>
    );
  }
}
